import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import Swal from 'sweetalert2';
import AccordionSection from '../../components/common/AccordionSection';
import { usePrendas } from '../../hooks/usePrendas';
import { useLocation } from 'react-router-dom';

const VisualizacionPrenda = ({ user, onLogout, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [prendaInfo, setPrendaInfo] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [anexoActual, setAnexoActual] = useState(1);
  const location = useLocation();
  const { loadPrendaInfo } = usePrendas();

  const formatearFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') return 'N/A';
    try {
      if (fecha.includes('T')) fecha = fecha.split('T')[0];
      if (fecha.includes('-')) {
        const partes = fecha.split('-');
        if (partes.length === 3 && partes[0].length === 4) return `${partes[2]}-${partes[1]}-${partes[0]}`;
      }
      return fecha;
    } catch { return fecha; }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        let casoData = location.state;
        if (!casoData) {
          const saved = localStorage.getItem('prendaData');
          if (saved) { try { casoData = JSON.parse(saved); } catch {} }
        }
        if (!casoData || (!casoData.group && !casoData.grupo)) {
          Swal.fire({ icon: 'warning', title: 'Sin datos', text: 'No se encontraron datos de la prenda para visualizar.', confirmButtonColor: '#ef4444' });
          setLoading(false);
          return;
        }
        const group = casoData.group || casoData.grupo;
        const order = casoData.order || casoData.orden;
        const caseType = casoData.fromDisponibles ? 'DISPONIBLE' : 'STORED';
        const data = await loadPrendaInfo(group, order, caseType);
        if (data) {
          setPrendaInfo({
            id: data.id || casoData.id || 0,
            group: data.group || group,
            order: data.order || order,
            titular: data.titular || casoData.titular || 'N/A',
            ident: data.ident || casoData.ident || 'N/A',
            sellerHouse: data.sellerHouse?.ceName || data.sellerHouse || casoData.sellerHouse || 'N/A',
            recordDate: formatearFecha(data.recordDate || casoData.recordDate || 'N/A'),
            vehiculeTag: data.vehiculeTag || casoData.vehiculeTag || 'N/A',
            vehiculeSerial: data.vehiculeSerial || casoData.vehiculeSerial || 'N/A',
            registerName: data.registerName || casoData.registerName || 'N/A',
            status: data.status || casoData.status || 'N/A',
          });
          if (Array.isArray(data.documents)) setDocumentos(data.documents);
        }
      } catch (error) {
        console.error('Error cargando prenda:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const documentoActual = documentos.length > 0 ? documentos[anexoActual - 1] : null;
  const [blobUrl, setBlobUrl] = useState(null);

  const buildDocUrl = (doc) => {
    if (!doc) return null;
    if (doc.documentPath) return doc.documentPath;
    if (doc.documentUrl) return doc.documentUrl;
    if (!doc.documentB64) return null;
    const lower = (doc.documentName || '').toLowerCase();
    const mime = lower.endsWith('.pdf') ? 'application/pdf'
      : lower.endsWith('.png') ? 'image/png'
      : lower.endsWith('.jpg') || lower.endsWith('.jpeg') ? 'image/jpeg'
      : 'application/pdf';
    return `data:${mime};base64,${doc.documentB64}`;
  };

  useEffect(() => {
    const rawUrl = buildDocUrl(documentoActual);
    if (!rawUrl) { setBlobUrl(null); return; }
    if (rawUrl.startsWith('data:')) { setBlobUrl(rawUrl); return; }
    let objUrl = null;
    const controller = new AbortController();
    fetch(rawUrl, { signal: controller.signal })
      .then(r => r.blob())
      .then(blob => { objUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })); setBlobUrl(objUrl); })
      .catch(() => setBlobUrl(rawUrl));
    return () => { controller.abort(); if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [documentoActual?.documentPath, documentoActual?.documentB64]);

  if (loading) {
    return (
      <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
        <div className="flex items-center justify-center h-screen">
          <HondaLoader message="Cargando prenda..." variant="fullscreen" size="large" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-4 py-4">
        {/* Título */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">Visualización de Prenda</h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Detalle completo de la prenda y su documentación</p>
        </div>

        {!prendaInfo ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
            <span className="material-icons text-4xl text-gray-300 mb-2">inventory_2</span>
            <p className="text-gray-500">No se pudieron cargar los datos de la prenda</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4">
            {/* ── Columna izquierda: visor de documentos ── */}
            <div className="flex-1 bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">
              <div className="h-2 bg-gray-800"></div>
              <div className="bg-gray-800 px-3 py-2 border-b border-gray-700">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div className="flex items-center gap-4">
                    <p className="text-xs text-white">
                      <span className="font-bold">Nombre del Anexo:</span> {documentoActual?.documentName || 'Sin documento'}
                    </p>
                    <p className="text-xs text-white">
                      <span className="font-bold">Anexo:</span> {anexoActual} / {documentos.length}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { if (anexoActual > 1) setAnexoActual(anexoActual - 1); }}
                      disabled={anexoActual === 1 || documentos.length === 0}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                      <span className="material-icons text-sm">chevron_left</span>Anterior
                    </button>
                    <button onClick={() => { if (anexoActual < documentos.length) setAnexoActual(anexoActual + 1); }}
                      disabled={anexoActual === documentos.length || documentos.length === 0}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                      Siguiente<span className="material-icons text-sm">chevron_right</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex-1 p-3 flex items-center justify-center bg-gray-100" style={{ minHeight: '550px' }}>
                {documentos.length === 0 ? (
                  <div className="text-center">
                    <span className="material-icons text-5xl text-gray-400 mb-2">description</span>
                    <p className="text-sm text-gray-500">No hay documentos disponibles</p>
                    <p className="text-xs text-gray-400 mt-1">Esta prenda no tiene documentos adjuntos</p>
                  </div>
                ) : blobUrl ? (
                  <iframe src={blobUrl} title={documentoActual?.documentName}
                    className="w-full h-full rounded-lg border border-gray-300" style={{ minHeight: '520px' }} />
                ) : (
                  <div className="text-center">
                    <span className="material-icons text-5xl text-gray-400 mb-2 block">pending</span>
                    <p className="text-sm text-gray-500">Documento pendiente de carga</p>
                    <p className="text-xs text-gray-400 mt-1">{documentoActual?.documentName}</p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Columna derecha: datos (35%) ── */}
            <div className="w-full lg:w-[35%] bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">
              <div className="h-2 bg-gray-800"></div>
              <div className="px-3 py-2 border-b border-gray-700 bg-gray-800 flex items-center justify-between">
                <p className="text-xs font-bold text-white">Prenda: {prendaInfo.group} / {prendaInfo.order}</p>
                {prendaInfo.status && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    prendaInfo.status.toUpperCase() === 'APROBADO' ? 'bg-green-500 text-white' :
                    prendaInfo.status.toUpperCase() === 'RECHAZADO' ? 'bg-red-500 text-white' :
                    prendaInfo.status.toUpperCase() === 'EN REVISION' ? 'bg-blue-500 text-white' :
                    'bg-gray-500 text-white'
                  }`}>{prendaInfo.status}</span>
                )}
              </div>
              <div className="flex-1 overflow-y-auto" style={{ maxHeight: '600px' }}>
                <AccordionSection title="IDENTIFICACIÓN" icon="inventory_2">
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-xs text-gray-500">Grupo</span>
                    <span className="text-xs font-medium text-gray-900">{prendaInfo.group}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-xs text-gray-500">Orden</span>
                    <span className="text-xs font-medium text-gray-900">{prendaInfo.order}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-xs text-gray-500">Nombre de Registro</span>
                    <span className="text-xs text-gray-800 text-right">{prendaInfo.registerName}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-xs text-gray-500">Fecha de Registro</span>
                    <span className="text-xs text-gray-800">{prendaInfo.recordDate}</span>
                  </div>
                </AccordionSection>

                <AccordionSection title="DATOS DEL TITULAR" icon="person">
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-xs text-gray-500">Titular</span>
                    <span className="text-xs font-medium text-gray-900 text-right">{prendaInfo.titular}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-xs text-gray-500">DNI / CUIT</span>
                    <span className="text-xs text-gray-800 font-mono">{prendaInfo.ident}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-xs text-gray-500">Concesionario</span>
                    <span className="text-xs text-gray-800 text-right">{prendaInfo.sellerHouse}</span>
                  </div>
                </AccordionSection>

                <AccordionSection title="DATOS DEL VEHÍCULO" icon="directions_car">
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-xs text-gray-500">Patente</span>
                    <span className="text-xs text-gray-800 font-mono">{prendaInfo.vehiculeTag}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-xs text-gray-500">N° de Serie / Chasis</span>
                    <span className="text-xs text-gray-800 font-mono text-right">{prendaInfo.vehiculeSerial}</span>
                  </div>
                </AccordionSection>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default VisualizacionPrenda;

